import React, { useId } from 'react';
import styles from './forms.module.scss';
import { ConfigProvider, DatePicker as AntdDatePicker } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { antdTheme as theme } from 'shared/constants';
import { DateTime } from 'luxon';

import luxonGenerateConfig from 'rc-picker/lib/generate/luxon';

/*
  We use luxon instead of the default antd date library (dayjs)
  To do so we created a custom DatePicker component
  https://ant.design/docs/react/use-custom-date-library#datepicker
*/
// In Ant Design v6, we need to pass the default export properly
const CustomDatePicker = AntdDatePicker.generatePicker(
  luxonGenerateConfig.default || luxonGenerateConfig,
);

const dateFormat = 'MMM D, YYYY';

/**
 * @typedef {import('antd').DatePickerProps} AntdDatePickerProps
 */

/**
 * @param {AntdDatePickerProps & { label?: string, error?: string, timeZone?: string, clearLabel?: string }} props
 * @returns {JSX.Element}
 */
function DatePicker({
  label = null,
  error = null,
  value,
  timeZone,
  onChange,
  id,
  allowClear = true,
  clearLabel = null,
  ...props
}) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const pickerValue = value ? DateTime.fromISO(value, timeZone ? { zone: timeZone } : {}) : '';

  // antd names the clear control from the icon's own glyph name ("close-circle"), which
  // tells a screen-reader user nothing about which field it clears. `CloseCircleFilled` is
  // antd's own default icon, so passing it back with a real label changes no pixels.
  const mergedAllowClear =
    clearLabel && allowClear
      ? { clearIcon: <CloseCircleFilled aria-label={clearLabel} /> }
      : allowClear;

  const handleChange = onChange
    ? (next, dateString) =>
        onChange(
          timeZone && next ? next.setZone(timeZone, { keepLocalTime: true }) : next,
          dateString,
        )
    : undefined;

  return (
    <ConfigProvider
      theme={{
        components: {
          DatePicker: {
            borderRadius: 8,
            colorBorder: theme.colorBorder,
            colorTextPlaceholder: theme.colorTextPlaceholder,
          },
        },
      }}>
      <div className={styles.root}>
        {label && <label htmlFor={inputId}>{label}</label>}
        <CustomDatePicker
          id={inputId}
          format={dateFormat}
          status={error && 'error'}
          value={pickerValue}
          onChange={handleChange}
          allowClear={mergedAllowClear}
          {...props}
        />
        <span className={styles.error}>{error}</span>
      </div>
    </ConfigProvider>
  );
}

export { DatePicker };
